import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '#/modules/commonComponents/button';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '#/modules/commonComponents/tabs';
import DashboardToneLegend from '#/modules/infra/dashboard/components/DashboardToneLegend';
import DashboardOverviewSection from '#/modules/infra/dashboard/components/DashboardOverviewSection';
import ModuleDashboardPanel from '#/modules/infra/dashboard/components/ModuleDashboardPanel';
import { useDashboardOverview } from '#/modules/infra/dashboard/hooks/useDashboardOverview';
import {
  DASHBOARD_GROUPS,
  DASHBOARD_MODULES,
  DASHBOARD_MODULE_MAP,
} from '#/modules/infra/dashboard/helper/dashboardModules';
import {
  DASHBOARD_MAX_MONTHS,
  countCalendarMonths,
  DASHBOARD_TREND_MONTH_OPTIONS,
  isCompleteRange,
  readPeriod,
  recentMonthsRange,
  shiftRangeWindow,
} from '#/modules/infra/dashboard/helper/dashboardRange';
import { getRequestErrorMessage } from '#/utils/requestError';

/**
 * 主控台（dashboard）。
 * 標題列右側（兩層）：日期區間；下一層統計日＋上個月／本月／下個月快捷鍵（上／下個月整段平移）。
 * 標題下方：分類 tabs（客戶／物料／生管），切換時同時換：
 *   - 該分類單據的狀態卡，一列兩欄；
 *   - 單據 tabs ＋ 月別狀態圖與（有財務可見性時）金額圓餅。
 * 全部資料來自一支 GET /dashboard/overview（含 status_by_month／amounts）；幣別整頁共用。
 * 日期區間＝單據期間＋完成期間（預設本月，Asia/Taipei）；待辦卡不限日期；月圖用 trend_months（3／6／12）。超過 120 個月前端先擋不送。
 * demo／開發預覽入口已移至「開發工具」（僅 dev）。
 */
const AdminHomePage = () => {
  // 日期區間＝單據期間（金額／分布）＋完成期間（完成卡），預設本月；待辦卡不看日期；月圖月數另由 trendMonths 控制
  const [range, setRange] = useState(() => recentMonthsRange(1));
  const [trendMonths, setTrendMonths] = useState(DASHBOARD_TREND_MONTH_OPTIONS[0]);
  const [draftRange, setDraftRange] = useState(null);
  const [activeGroup, setActiveGroup] = useState('');
  const [activeModuleByGroup, setActiveModuleByGroup] = useState({});
  const [currencyId, setCurrencyId] = useState('');
  const tooLong = countCalendarMonths(range) > DASHBOARD_MAX_MONTHS;

  const overviewQuery = useDashboardOverview(range, { currencyId, trendMonths, enabled: !tooLong });
  const modules = overviewQuery.data;

  const defaultPeriod = useMemo(() => {
    const firstKey = DASHBOARD_MODULES.find((config) => modules?.[config.key])?.key;
    return firstKey ? readPeriod(firstKey, modules[firstKey].period) : null;
  }, [modules]);

  // overview 有 key 的 module 才顯示（未授權 module 由後端省略）
  const detailGroups = useMemo(
    () => DASHBOARD_GROUPS
      .map((group) => ({
        ...group,
        configs: group.moduleKeys
          .map((key) => DASHBOARD_MODULE_MAP[key])
          .filter((config) => Boolean(modules?.[config.key])),
      }))
      .filter((group) => group.configs.length > 0),
    [modules],
  );
  const currentGroup = detailGroups.find((group) => group.key === activeGroup) ?? detailGroups[0];

  const periodValue = defaultPeriod ? { from: defaultPeriod.from, to: defaultPeriod.to } : undefined;
  const dateValue = draftRange ?? range ?? periodValue;
  const defaultRange = recentMonthsRange(1);
  const isDefaultRange = range?.from === defaultRange.from && range?.to === defaultRange.to;

  const applyRange = (next) => {
    setDraftRange(null);
    setRange(next);
  };

  const handleRangeChange = (next) => {
    setDraftRange(next);
    if (isCompleteRange(next)) {
      setRange({ from: next.from, to: next.to });
    }
  };

  return (
    <Tabs value={currentGroup?.key ?? ''} onValueChange={setActiveGroup} className="@container flex flex-col gap-3">
      {/*
        寬版 grid：左上＝標題、左下＝分類 tabs＋狀態色說明、右＝日期區塊（跨兩列）。
        窄版單欄：標題 → 日期 → 分類 tabs → 狀態色。
      */}
      <div className="grid grid-cols-1 gap-x-4 gap-y-3 @3xl:grid-cols-[minmax(0,1fr)_auto]">
        <h1 className="page_title mb-0! @3xl:col-start-1 @3xl:row-start-1">
          <span className="border-l-5 border-primary-400 pl-2.5">主控台</span>
        </h1>
        {/* 日期區間 → 統計日＋月份快捷鍵；寬版跨兩列（標題列＋tabs 列） */}
        <div
          className="
            flex w-full flex-col gap-1.5 pt-3
            @3xl:col-start-2 @3xl:row-span-2 @3xl:row-start-1 @3xl:w-md @3xl:self-center
          "
        >
          <FloatingDateInput
            id="dashboard-date-range"
            label="日期區間"
            tip="單據期間（金額、分布圖依各單據日期：銷貨日、訂單日、採購日、進貨日、出庫日、工單排程開始日）與完成期間（完成卡依完成日期）共用此區間，預設本月。目前待辦不受日期限制；月別圖月數在圖上切換。"
            mode="range"
            value={dateValue}
            onChange={handleRangeChange}
            containerClassName="w-full"
            numberOfMonths={2}
            collapseEmptyError
          />
          <div className="flex items-center justify-between gap-2">
            <p className="truncate text-xs text-muted-foreground tabular-nums" title="Asia/Taipei">
              {defaultPeriod?.asOfDate ? `統計日 ${defaultPeriod.asOfDate}` : ''}
            </p>
            <div className="flex shrink-0 gap-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!isCompleteRange(range)}
                onClick={() => applyRange(shiftRangeWindow(range, -1))}
              >
                <ChevronLeft className="size-4" aria-hidden />
                上個月
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isDefaultRange}
                onClick={() => applyRange(recentMonthsRange(1))}
              >
                本月
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!isCompleteRange(range)}
                onClick={() => applyRange(shiftRangeWindow(range, 1))}
              >
                下個月
                <ChevronRight className="size-4" aria-hidden />
              </Button>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2 @3xl:col-start-1 @3xl:row-start-2">
          {currentGroup && (
            <TabsList className="justify-start">
              {detailGroups.map((group) => (
                <TabsTrigger key={group.key} value={group.key}>
                  {group.title}
                </TabsTrigger>
              ))}
            </TabsList>
          )}
          {currentGroup && <DashboardToneLegend />}
        </div>
      </div>

      {tooLong && (
        <p className="text-sm text-muted-foreground">
          主控台最多查詢 {DASHBOARD_MAX_MONTHS} 個月，請縮短日期區間。
        </p>
      )}
      {!tooLong && overviewQuery.isPending && <p className="text-sm text-muted-foreground">讀取中…</p>}
      {!tooLong && overviewQuery.isError && (
        <div className="flex flex-col items-start gap-2 text-sm">
          <p className="text-destructive">{getRequestErrorMessage(overviewQuery.error, '讀取主控台失敗')}</p>
          {currencyId && (
            <Button type="button" variant="outline" size="sm" onClick={() => setCurrencyId('')}>
              改回預設幣別
            </Button>
          )}
        </div>
      )}
      {!tooLong && overviewQuery.isSuccess && !currentGroup && (
        <p className="rounded-lg border border-border bg-background p-4 text-sm text-muted-foreground">
          目前帳號沒有可顯示的主控台模組。
        </p>
      )}

      {/* 超過上限時不送 overview；隱藏前一次（keepPreviousData）的舊圖，避免與所選區間不符 */}
      {!tooLong && detailGroups.map((group) => {
        const moduleKey = group.configs.some((config) => config.key === activeModuleByGroup[group.key])
          ? activeModuleByGroup[group.key]
          : group.configs[0].key;
        return (
          <TabsContent key={group.key} value={group.key} className="@container flex flex-col gap-4">
            <DashboardOverviewSection group={group} modules={modules} range={range} />
            <Tabs
              value={moduleKey}
              onValueChange={(next) => setActiveModuleByGroup((prev) => ({ ...prev, [group.key]: next }))}
              className="flex flex-col gap-3"
            >
              <TabsList className="justify-start">
                {group.configs.map((config) => (
                  <TabsTrigger key={config.key} value={config.key}>
                    {config.title}
                  </TabsTrigger>
                ))}
              </TabsList>
              {group.configs.map((config) => (
                <TabsContent key={config.key} value={config.key} className="@container">
                  <ModuleDashboardPanel
                    config={config}
                    moduleData={modules[config.key]}
                    currencyId={currencyId}
                    onCurrencyChange={setCurrencyId}
                    trendMonths={trendMonths}
                    onTrendMonthsChange={setTrendMonths}
                    isFetching={overviewQuery.isFetching}
                  />
                </TabsContent>
              ))}
            </Tabs>
          </TabsContent>
        );
      })}
    </Tabs>
  );
};

export default AdminHomePage;
